import { moneyFromDecimalString, moneyToDecimalString } from '@ibe/utils';
import { z } from 'zod';

/**
 * Promotion type and platform display options.
 *
 * Kept local to the admin module so the portal does not need to depend on
 * `@ibe/pricing`; the values are the same as the engine's `PROMOTION_TYPE` and
 * `PROMOTION_PLATFORM` vocabularies.
 */
export const PROMOTION_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'PERCENTAGE', label: 'Percentage discount' },
  { value: 'FIXED_AMOUNT', label: 'Fixed amount off' },
  { value: 'STAY_X_PAY_Y', label: 'Stay X pay Y' },
  { value: 'EARLY_BIRD', label: 'Early bird' },
  { value: 'LAST_MINUTE', label: 'Last minute' },
  { value: 'LONG_STAY', label: 'Long stay' },
  { value: 'MEMBER', label: 'Member' },
  { value: 'FIXED_RATE', label: 'Fixed rate' },
] as const;

export const PROMOTION_PLATFORM_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'ALL', label: 'All channels' },
  { value: 'WEB', label: 'Web only' },
  { value: 'MOBILE', label: 'Mobile only' },
] as const;

const CODE_MIN_LENGTH = 2;
const CODE_MAX_LENGTH = 20;
const CODE_PATTERN = new RegExp(
  `^[A-Z0-9-]{${String(CODE_MIN_LENGTH)},${String(CODE_MAX_LENGTH)}}$`,
  'u',
);
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/u;
const POSITIVE_INT_PATTERN = /^\d+$/u;
const DECIMAL_PATTERN = /^\d+(\.\d+)?$/u;
const PERCENTAGE_PATTERN = /^\d+(\.\d+)?$/u;
const NAME_MAX_LENGTH = 255;
const CURRENCY_CODE_LENGTH = 3;
const PERCENT_MULTIPLIER = 100;
const PERCENT_FRACTION_DIGITS = 4;

export function splitIds(value: string | undefined): readonly string[] {
  if (value === undefined) return [];
  return value
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id !== '');
}

export function joinIds(ids: readonly string[] | undefined): string {
  return (ids ?? []).join(', ');
}

/** Convert a stored percentage such as "25/100" or "255/1000" to a decimal percent. */
function rationalToPercent(value: string | null): string {
  if (value === null) return '';
  const [num, den] = value.split('/');
  if (num === undefined || den === undefined) return '';
  const numerator = Number(num);
  const denominator = Number(den);
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) return '';
  const percent = (numerator / denominator) * PERCENT_MULTIPLIER;
  const fixed = percent.toFixed(PERCENT_FRACTION_DIGITS);
  return fixed.replace(/\.?0+$/u, '');
}

/** Convert a stored minor-unit amount to a decimal string for the form. */
function minorToDecimal(minor: string | null, currency: string): string {
  if (minor === null) return '';
  try {
    return moneyToDecimalString({ amountMinor: BigInt(minor), currency });
  } catch {
    return '';
  }
}

/** Convert a decimal amount string to a minor-unit string for the API. */
function decimalToMinor(value: string | undefined, currency: string): string | null {
  if (value === undefined || value.trim() === '') return null;
  try {
    return String(moneyFromDecimalString(value, currency).amountMinor);
  } catch {
    return null;
  }
}

const fixedRateOverride = z.object({
  propertyId: z.string().nullable(),
  roomTypeId: z.string().nullable(),
  ratePlanId: z.string().nullable(),
  fixedRate: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
});

export const promotionDetail = z.object({
  id: z.string(),
  propertyId: z.string().nullable(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  priority: z.number(),
  currency: z.string(),
  percentage: z.string().nullable(),
  amount: z.string().nullable(),
  stayNights: z.number().nullable(),
  payNights: z.number().nullable(),
  minAdvanceDays: z.number().nullable(),
  maxAdvanceDays: z.number().nullable(),
  minNights: z.number().nullable(),
  maxDiscount: z.string().nullable(),
  combinable: z.boolean(),
  exclusive: z.boolean(),
  enabled: z.boolean(),
  bookableFrom: z.string().nullable(),
  bookableTo: z.string().nullable(),
  stayFrom: z.string().nullable(),
  stayTo: z.string().nullable(),
  ratePlanIds: z.array(z.string()),
  roomTypeIds: z.array(z.string()),
  description: z.string().nullable(),
  customerFacingName: z.string().nullable(),
  promoCodeRequired: z.boolean(),
  applyToAllRates: z.boolean(),
  visibilityFrom: z.string().nullable(),
  visibilityTo: z.string().nullable(),
  pmsPromoCode: z.string().nullable(),
  platform: z.string(),
  promotionLimitTime: z.boolean(),
  promotionLimitStartTime: z.string().nullable(),
  promotionLimitEndTime: z.string().nullable(),
  excludedEnabled: z.boolean(),
  excludeStartDate: z.string().nullable(),
  excludeEndDate: z.string().nullable(),
  inclusionDays: z.array(z.number()),
  showAllRates: z.boolean(),
  showOnlyThisPromo: z.boolean(),
  fixedRate: z.string().nullable(),
  stackingBreakdown: z.record(z.string(), z.unknown()),
  continuesPromotion: z.boolean(),
  mobileDiscount: z.record(z.string(), z.unknown()),
  excludedDates: z.array(z.string()),
  inclusionPackageIds: z.array(z.string()),
  stacksWithIds: z.array(z.string()),
  fixedRateOverrides: z.array(fixedRateOverride),
  createdAt: z.string().optional(),
  version: z.number(),
});

export type PromotionDetail = z.infer<typeof promotionDetail>;

export const promotionRow = promotionDetail.partial().required({ id: true });

export type PromotionRow = z.infer<typeof promotionRow>;

export type PromotionType = (typeof PROMOTION_TYPE_OPTIONS)[number]['value'];
export type PromotionPlatform = (typeof PROMOTION_PLATFORM_OPTIONS)[number]['value'];

/**
 * The form collects values as strings (controlled inputs) and arrays as
 * comma-separated tokens (a simple, keyboard-friendly picker until a dedicated
 * multi-select component is available).
 */
export const promotionFormInput = z
  .object({
    version: z.string().optional(),
    propertyId: z.string().optional(),
    code: z
      .string()
      .trim()
      .toUpperCase()
      .min(1, 'A promotion code is required')
      .max(CODE_MAX_LENGTH, 'Keep the code under 20 characters')
      .regex(CODE_PATTERN, 'Use 2–20 uppercase letters, numbers or hyphens'),
    name: z.string().trim().min(1, 'A promotion name is required').max(NAME_MAX_LENGTH),
    type: z.string().min(1, 'Choose a promotion type'),
    priority: z.string().regex(POSITIVE_INT_PATTERN, 'Priority must be a whole number'),
    currency: z
      .string()
      .length(CURRENCY_CODE_LENGTH, 'Currency must be a three-letter code')
      .toUpperCase(),
    percentage: z.string().optional(),
    amount: z.string().optional(),
    stayNights: z.string().optional(),
    payNights: z.string().optional(),
    minAdvanceDays: z.string().optional(),
    maxAdvanceDays: z.string().optional(),
    minNights: z.string().optional(),
    maxDiscount: z.string().optional(),
    combinable: z.boolean(),
    exclusive: z.boolean(),
    enabled: z.boolean(),
    bookableFrom: z.string().optional(),
    bookableTo: z.string().optional(),
    stayFrom: z.string().optional(),
    stayTo: z.string().optional(),
    ratePlanInput: z.string().optional(),
    roomTypeInput: z.string().optional(),
    description: z.string().optional(),
    customerFacingName: z.string().optional(),
    promoCodeRequired: z.boolean(),
    applyToAllRates: z.boolean(),
    visibilityFrom: z.string().optional(),
    visibilityTo: z.string().optional(),
    pmsPromoCode: z.string().optional(),
    platform: z.string(),
    promotionLimitTime: z.boolean(),
    promotionLimitStartTime: z.string().optional(),
    promotionLimitEndTime: z.string().optional(),
    excludedEnabled: z.boolean(),
    excludeStartDate: z.string().optional(),
    excludeEndDate: z.string().optional(),
    inclusionDaysInput: z.string().optional(),
    showAllRates: z.boolean(),
    showOnlyThisPromo: z.boolean(),
    fixedRate: z.string().optional(),
    continuesPromotion: z.boolean(),
    excludedDatesInput: z.string().optional(),
    inclusionPackageInput: z.string().optional(),
    stacksWithInput: z.string().optional(),
    fixedRateOverrides: z.array(fixedRateOverride),
  })
  .superRefine((values, ctx) => {
    const currency = values.currency;

    function checkDate(field: string, value: string | undefined, label: string): void {
      if (value !== undefined && value !== '' && !DATE_PATTERN.test(value)) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${label} must be a date such as 2026-12-31`,
        });
      }
    }

    function checkTime(field: string, value: string | undefined, label: string): void {
      if (value !== undefined && value !== '' && !TIME_PATTERN.test(value)) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${label} must be a time such as 14:00`,
        });
      }
    }

    function checkInteger(field: string, value: string | undefined, label: string): void {
      if (value !== undefined && value !== '' && !POSITIVE_INT_PATTERN.test(value)) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${label} must be a whole number`,
        });
      }
    }

    function checkDecimal(field: string, value: string | undefined, label: string): void {
      if (value !== undefined && value !== '' && !DECIMAL_PATTERN.test(value)) {
        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: `${label} must be a decimal number`,
        });
      }
      if (value !== undefined && value !== '') {
        try {
          moneyFromDecimalString(value, currency);
        } catch {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: `${label} is not a valid amount for ${currency}`,
          });
        }
      }
    }

    checkDate('bookableFrom', values.bookableFrom, 'Bookable from');
    checkDate('bookableTo', values.bookableTo, 'Bookable to');
    checkDate('stayFrom', values.stayFrom, 'Stay from');
    checkDate('stayTo', values.stayTo, 'Stay to');
    checkDate('visibilityFrom', values.visibilityFrom, 'Visibility from');
    checkDate('visibilityTo', values.visibilityTo, 'Visibility to');
    checkDate('excludeStartDate', values.excludeStartDate, 'Exclusion start');
    checkDate('excludeEndDate', values.excludeEndDate, 'Exclusion end');
    checkTime('promotionLimitStartTime', values.promotionLimitStartTime, 'Limit start time');
    checkTime('promotionLimitEndTime', values.promotionLimitEndTime, 'Limit end time');
    checkInteger('stayNights', values.stayNights, 'Stay nights');
    checkInteger('payNights', values.payNights, 'Pay nights');
    checkInteger('minAdvanceDays', values.minAdvanceDays, 'Minimum advance days');
    checkInteger('maxAdvanceDays', values.maxAdvanceDays, 'Maximum advance days');
    checkInteger('minNights', values.minNights, 'Minimum nights');
    checkDecimal('amount', values.amount, 'Amount off');
    checkDecimal('maxDiscount', values.maxDiscount, 'Maximum discount');
    checkDecimal('fixedRate', values.fixedRate, 'Fixed rate');

    if (
      values.percentage !== undefined &&
      values.percentage !== '' &&
      !PERCENTAGE_PATTERN.test(values.percentage)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['percentage'],
        message: 'Percentage must be a number such as 25.5',
      });
    }

    if (
      values.bookableFrom !== undefined &&
      values.bookableTo !== undefined &&
      values.bookableFrom !== '' &&
      values.bookableTo !== '' &&
      values.bookableFrom > values.bookableTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['bookableTo'],
        message: 'Bookable to must be on or after bookable from',
      });
    }
    if (
      values.stayFrom !== undefined &&
      values.stayTo !== undefined &&
      values.stayFrom !== '' &&
      values.stayTo !== '' &&
      values.stayFrom > values.stayTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['stayTo'],
        message: 'Stay to must be on or after stay from',
      });
    }
  });

export type PromotionFormInput = z.infer<typeof promotionFormInput>;

export const CREATE_DEFAULTS: PromotionFormInput = {
  version: undefined,
  propertyId: '',
  code: '',
  name: '',
  type: 'PERCENTAGE',
  priority: '0',
  currency: 'AUD',
  percentage: '',
  amount: '',
  stayNights: '',
  payNights: '',
  minAdvanceDays: '',
  maxAdvanceDays: '',
  minNights: '',
  maxDiscount: '',
  combinable: false,
  exclusive: false,
  enabled: true,
  bookableFrom: '',
  bookableTo: '',
  stayFrom: '',
  stayTo: '',
  ratePlanInput: '',
  roomTypeInput: '',
  description: '',
  customerFacingName: '',
  promoCodeRequired: false,
  applyToAllRates: true,
  visibilityFrom: '',
  visibilityTo: '',
  pmsPromoCode: '',
  platform: 'ALL',
  promotionLimitTime: false,
  promotionLimitStartTime: '',
  promotionLimitEndTime: '',
  excludedEnabled: false,
  excludeStartDate: '',
  excludeEndDate: '',
  inclusionDaysInput: '',
  showAllRates: true,
  showOnlyThisPromo: false,
  fixedRate: '',
  continuesPromotion: false,
  excludedDatesInput: '',
  inclusionPackageInput: '',
  stacksWithInput: '',
  fixedRateOverrides: [],
};

export function defaultsFrom(detail: PromotionDetail): PromotionFormInput {
  const currency = detail.currency;

  const overrideDefaults = detail.fixedRateOverrides.map((override) => ({
    ...override,
    fixedRate: minorToDecimal(override.fixedRate, currency),
  }));

  return {
    version: String(detail.version),
    propertyId: detail.propertyId ?? '',
    code: detail.code,
    name: detail.name,
    type: detail.type,
    priority: String(detail.priority),
    currency,
    percentage: rationalToPercent(detail.percentage),
    amount: minorToDecimal(detail.amount, currency),
    stayNights: detail.stayNights === null ? '' : String(detail.stayNights),
    payNights: detail.payNights === null ? '' : String(detail.payNights),
    minAdvanceDays: detail.minAdvanceDays === null ? '' : String(detail.minAdvanceDays),
    maxAdvanceDays: detail.maxAdvanceDays === null ? '' : String(detail.maxAdvanceDays),
    minNights: detail.minNights === null ? '' : String(detail.minNights),
    maxDiscount: minorToDecimal(detail.maxDiscount, currency),
    combinable: detail.combinable,
    exclusive: detail.exclusive,
    enabled: detail.enabled,
    bookableFrom: detail.bookableFrom ?? '',
    bookableTo: detail.bookableTo ?? '',
    stayFrom: detail.stayFrom ?? '',
    stayTo: detail.stayTo ?? '',
    ratePlanInput: joinIds(detail.ratePlanIds),
    roomTypeInput: joinIds(detail.roomTypeIds),
    description: detail.description ?? '',
    customerFacingName: detail.customerFacingName ?? '',
    promoCodeRequired: detail.promoCodeRequired,
    applyToAllRates: detail.applyToAllRates,
    visibilityFrom: detail.visibilityFrom ?? '',
    visibilityTo: detail.visibilityTo ?? '',
    pmsPromoCode: detail.pmsPromoCode ?? '',
    platform: detail.platform,
    promotionLimitTime: detail.promotionLimitTime,
    promotionLimitStartTime: detail.promotionLimitStartTime ?? '',
    promotionLimitEndTime: detail.promotionLimitEndTime ?? '',
    excludedEnabled: detail.excludedEnabled,
    excludeStartDate: detail.excludeStartDate ?? '',
    excludeEndDate: detail.excludeEndDate ?? '',
    inclusionDaysInput: detail.inclusionDays.join(', '),
    showAllRates: detail.showAllRates,
    showOnlyThisPromo: detail.showOnlyThisPromo,
    fixedRate: minorToDecimal(detail.fixedRate, currency),
    continuesPromotion: detail.continuesPromotion,
    excludedDatesInput: joinIds(detail.excludedDates),
    inclusionPackageInput: joinIds(detail.inclusionPackageIds),
    stacksWithInput: joinIds(detail.stacksWithIds),
    fixedRateOverrides: overrideDefaults,
  };
}

function overridesToApi(overrides: PromotionFormInput['fixedRateOverrides'], currency: string) {
  return overrides.map((override) => ({
    propertyId: override.propertyId === '' ? null : override.propertyId,
    roomTypeId: override.roomTypeId === '' ? null : override.roomTypeId,
    ratePlanId: override.ratePlanId === '' ? null : override.ratePlanId,
    fixedRate: decimalToMinor(override.fixedRate, currency) ?? '0',
    startDate: override.startDate === '' ? null : override.startDate,
    endDate: override.endDate === '' ? null : override.endDate,
  }));
}

function optionalNumber(value: string | undefined): number | null | undefined {
  if (value === undefined || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function optionalDate(value: string | undefined): string | null | undefined {
  if (value === undefined || value === '') return null;
  return value;
}

export function createPromotionPayload(values: PromotionFormInput): Record<string, unknown> {
  const currency = values.currency;

  return {
    propertyId: values.propertyId === '' ? null : values.propertyId,
    code: values.code.toUpperCase(),
    name: values.name,
    type: values.type,
    priority: Number(values.priority),
    currency,
    percentage: values.percentage === '' ? null : values.percentage,
    amount: decimalToMinor(values.amount, currency),
    stayNights: optionalNumber(values.stayNights),
    payNights: optionalNumber(values.payNights),
    minAdvanceDays: optionalNumber(values.minAdvanceDays),
    maxAdvanceDays: optionalNumber(values.maxAdvanceDays),
    minNights: optionalNumber(values.minNights),
    maxDiscount: decimalToMinor(values.maxDiscount, currency),
    combinable: values.combinable,
    exclusive: values.exclusive,
    enabled: values.enabled,
    bookableFrom: optionalDate(values.bookableFrom),
    bookableTo: optionalDate(values.bookableTo),
    stayFrom: optionalDate(values.stayFrom),
    stayTo: optionalDate(values.stayTo),
    ratePlanIds: splitIds(values.ratePlanInput),
    roomTypeIds: splitIds(values.roomTypeInput),
    description: values.description === '' ? null : values.description,
    customerFacingName: values.customerFacingName === '' ? null : values.customerFacingName,
    promoCodeRequired: values.promoCodeRequired,
    applyToAllRates: values.applyToAllRates,
    visibilityFrom: optionalDate(values.visibilityFrom),
    visibilityTo: optionalDate(values.visibilityTo),
    pmsPromoCode: values.pmsPromoCode === '' ? null : values.pmsPromoCode,
    platform: values.platform,
    promotionLimitTime: values.promotionLimitTime,
    promotionLimitStartTime:
      values.promotionLimitStartTime === '' ? null : values.promotionLimitStartTime,
    promotionLimitEndTime:
      values.promotionLimitEndTime === '' ? null : values.promotionLimitEndTime,
    excludedEnabled: values.excludedEnabled,
    excludeStartDate: optionalDate(values.excludeStartDate),
    excludeEndDate: optionalDate(values.excludeEndDate),
    inclusionDays: splitIds(values.inclusionDaysInput).map(Number),
    showAllRates: values.showAllRates,
    showOnlyThisPromo: values.showOnlyThisPromo,
    fixedRate: decimalToMinor(values.fixedRate, currency),
    continuesPromotion: values.continuesPromotion,
    stackingBreakdown: {},
    mobileDiscount: {},
    excludedDates: splitIds(values.excludedDatesInput),
    inclusionPackageIds: splitIds(values.inclusionPackageInput),
    stacksWithIds: splitIds(values.stacksWithInput),
    fixedRateOverrides: overridesToApi(values.fixedRateOverrides, currency),
  };
}

export function updatePromotionPayload(
  values: PromotionFormInput,
  version: number,
): Record<string, unknown> {
  const payload = createPromotionPayload(values);
  return { ...payload, version };
}
